'use client';

import { formatDistanceToNow } from 'date-fns';
import { signIn } from 'next-auth/react';
import { AccountType } from '@/autogenerated/@prisma/prisma-mongo-client';
import AdminUserManagement from '@/components/features/admin/AdminUserManagement';
import AdminUserMatches from '@/components/features/admin/AdminUserMatches';
import Avatar from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

interface AccountActionsDialogProps {
  user: {
    id: string;
    name?: string | null;
    photo?: string | null;
    accountType: AccountType | null;
    lastLoginDate: string | null;
  };
}

export function AccountActionsDialog({ user }: AccountActionsDialogProps) {
  const formattedLastLogin = user.lastLoginDate
    ? formatDistanceToNow(new Date(user.lastLoginDate), { addSuffix: true })
    : 'Never';

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant='secondary' size='medium'>
          Actions
        </Button>
      </DialogTrigger>
      <DialogContent
        className='max-h-[80vh] items-start overflow-y-auto md:max-w-[850px]'
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle className='flex items-center gap-4'>
            <Avatar className='h-16 w-16' imageUrl={user.photo ?? undefined} />
            {user.name}
            <span className='text-xs font-normal text-gray-500'>
              id: {user.id}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className='mb-4 text-sm text-gray-500'>
          Last login: {formattedLastLogin}
        </div>

        <Button
          onClick={async () => {
            await signIn('admin-login', {
              userId: user.id,
            });
          }}
        >
          Login as {user.name}
        </Button>
        {user.accountType && (
          <AdminUserMatches
            userId={user.id}
            accountType={user.accountType}
            userName={user.name?.split(' ')[0] ?? ''}
          />
        )}
        <AdminUserManagement userId={user.id} />
      </DialogContent>
    </Dialog>
  );
}
